import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import FilterBar, { type FilterValues } from "@/components/feature/FilterBar";
import Pagination from "@/components/base/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  allMedia,
  filterMedia,
  regions,
  sortMedia,
  years,
  type SortKey,
} from "@/lib/media";
import type { MediaKind } from "@/types/media";

interface MediaCatalogProps {
  kind: MediaKind;
  title: string;
  subtitle: string;
  intro: string;
  genreOptions: string[];
  meta: { title: string; description: string; keywords: string };
  breadcrumb: string;
}

const PAGE_SIZE = 8;

export default function MediaCatalog({
  kind,
  title,
  subtitle,
  intro,
  genreOptions,
  meta,
  breadcrumb,
}: MediaCatalogProps) {
  usePageMeta(meta);

  const [searchParams, setSearchParams] = useSearchParams();

  const values: FilterValues = {
    genre: searchParams.get("genre") || "",
    region: searchParams.get("region") || "",
    year: searchParams.get("year") || "",
    sort: searchParams.get("sort") || "default",
  };
  const page = Math.max(1, Number(searchParams.get("page") || "1"));

  const updateFilters = (patch: Partial<FilterValues>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value && value !== "default") next.set(key, String(value));
      else next.delete(key);
    });
    next.delete("page");
    setSearchParams(next);
  };

  const handleReset = () => setSearchParams({});

  const handlePageChange = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const kindItems = allMedia.filter((item) => item.kind === kind);
  const filtered = filterMedia(kindItems, values);
  const sorted = sortMedia(filtered, values.sort as SortKey);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-500">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{breadcrumb}</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {title}
        </h1>
        <h2 className="mt-1.5 font-heading text-sm font-medium uppercase tracking-wider text-primary-500">
          {subtitle}
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{intro}</p>
      </header>

      <div className="mt-6">
        <FilterBar
          genreOptions={genreOptions}
          regionOptions={regions}
          yearOptions={years}
          values={values}
          onChange={updateFilters}
          onReset={handleReset}
          resultCount={sorted.length}
        />
      </div>

      {paged.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {paged.map((item) => (
            <MediaCard key={item.id} media={item} showSynopsis />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-200/70 bg-background-100/40 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/60 text-foreground-500">
            <i className="ri-search-2-line text-2xl"></i>
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
            没有找到符合条件的影片
          </p>
          <p className="mt-1.5 text-sm text-foreground-500">试试更换筛选条件，或重置后重新浏览</p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            重置筛选
          </button>
        </div>
      )}

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
}